import { DateOnly } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledStringDateOnlyPicker,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Grid, MenuItem, Tooltip } from '@mui/material'
import { useGetFirstPaymentDateOptions } from '@src/data/api/credit-application/credit-application-api'
import { useValidTerms } from '@src/data/hooks/terms-hooks'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { formatPaymentPlanForList } from '@src/services/Formatter'
import { memo, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control, FieldPath, FieldPathByValue, FieldValues, useController, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FieldReadyGate } from './BaseWorksheetForm.components'
import {
  BaseWorksheetFormConfig,
  BaseWorksheetFormConfigProvider,
  findMissingFinancingConfigKeys,
  useBaseWorksheetFormConfig,
} from './BaseWorksheetForm.config'
import {
  createLoadingStore,
  FieldPrerequisiteSpec,
  getMaxPmtAmountWithAgentLeeway,
  isPromoPlan,
  LoadingStoreContext,
  useReportFieldLoading,
} from './BaseWorksheetForm.helpers'
import {
  areWorksheetErrorsEqual,
  ErrorRegistry,
  ErrorRegistryProvider,
  useReportError,
  WorksheetError,
} from './BaseWorksheetForm.validators'

type TranslateFn = (key: string) => string
const cellSize = { xs: 12, md: 4 } as const
const SHORT_TERM_CAP = 72

// Above this many API options, the dropdown becomes harder to scan than a calendar.
const FIRST_PAYMENT_DATE_PICKER_THRESHOLD = 12

const translateInvalidTermReason = (t: TranslateFn, reason: string | undefined): string => {
  switch (reason) {
    case 'pmtTooHigh':
      return t('worksheetCommon.errors.pmtTooHigh')
    default:
      return reason ?? ''
  }
}

interface BaseWorksheetFormProps {
  config: BaseWorksheetFormConfig
  children: ReactNode
  onLoadingChange?: (isLoading: boolean) => void
  onError?: (errors: WorksheetError[]) => void
}

const BaseWorksheetForm = ({ config, children, onLoadingChange, onError }: BaseWorksheetFormProps) => {
  const loadingStore = useMemo(() => createLoadingStore(), [])

  // Mirror via ref so the wrapper doesn't re-render on every per-id flip.
  const onLoadingChangeRef = useRef(onLoadingChange)
  onLoadingChangeRef.current = onLoadingChange
  useEffect(() => {
    let last = loadingStore.isAnyLoading()
    onLoadingChangeRef.current?.(last)
    return loadingStore.subscribe(() => {
      const next = loadingStore.isAnyLoading()
      if (next === last) return
      last = next
      onLoadingChangeRef.current?.(next)
    })
  }, [loadingStore])

  const [errorMap, setErrorMap] = useState<Record<string, WorksheetError>>({})

  const registerError = useCallback<ErrorRegistry>((id, error) => {
    setErrorMap((prev) => {
      if (error === null) {
        if (!(id in prev)) return prev
        const next = { ...prev }
        delete next[id]
        return next
      }
      const existing = prev[id]
      if (existing && areWorksheetErrorsEqual(existing, error)) return prev
      return { ...prev, [id]: error }
    })
  }, [])

  const errors = useMemo(() => Object.values(errorMap), [errorMap])
  useEffect(() => {
    onError?.(errors)
  }, [errors, onError])

  const missing = useMemo(() => findMissingFinancingConfigKeys(config), [config])
  const isConfigValid = missing.length === 0
  useEffect(() => {
    if (!isConfigValid) {
      registerError('configInvalid', {
        code: 'configInvalid',
        messageKey: 'worksheetCommon.errors.configInvalid',
        messageArgs: { missing: missing.join(', ') },
      })
    } else {
      registerError('configInvalid', null)
    }
  }, [registerError, isConfigValid, missing])

  return (
    <BaseWorksheetFormConfigProvider value={config}>
      <ErrorRegistryProvider value={registerError}>
        <LoadingStoreContext.Provider value={loadingStore}>
          <Grid container spacing={2.5}>
            {children}
          </Grid>
        </LoadingStoreContext.Provider>
      </ErrorRegistryProvider>
    </BaseWorksheetFormConfigProvider>
  )
}

interface AmountRequestedProps<T extends FieldValues & { amountRequested: number }> {
  control: Control<T>
  label?: string
  disabled?: boolean
}

const AmountRequested = <T extends FieldValues & { amountRequested: number }>({
  control,
  label,
  disabled,
}: AmountRequestedProps<T>) => {
  const { t } = useTranslation()
  return (
    <Grid size={cellSize}>
      <ControlledNumberField
        name={'amountRequested' as FieldPathByValue<T, number>}
        control={control}
        label={label ?? t('worksheetCommon.amountRequested')}
        isMoney
        disabled={disabled}
        fullWidth
      />
    </Grid>
  )
}

interface PaymentPlanProps<T extends FieldValues & { paymentPlanId: string; term: number | null }> {
  control: Control<T>
  label?: string
}

const PaymentPlan = <T extends FieldValues & { paymentPlanId: string; term: number | null }>({
  control,
  label,
}: PaymentPlanProps<T>) => {
  const { t } = useTranslation()
  const { plans, availablePaymentPlans } = useBaseWorksheetFormConfig()

  const paymentPlanIdName = 'paymentPlanId' as FieldPathByValue<T, string>
  const currentPaymentPlanId = useWatch({ control, name: paymentPlanIdName }) as string | undefined

  const options = useMemo(() => {
    const items: ReactElement[] = [
      <MenuItem key={EPaymentPlan.RegularDailyInterests} value={EPaymentPlan.RegularDailyInterests}>
        {t('EPaymentPlan.regularInterest', { ns: 'enum' })}
      </MenuItem>,
    ]

    const seenIds = new Set<string>([EPaymentPlan.RegularDailyInterests])
    for (const plan of availablePaymentPlans) {
      if (seenIds.has(plan.id as string)) continue
      seenIds.add(plan.id as string)
      items.push(
        <MenuItem key={plan.id} value={plan.id}>
          {formatPaymentPlanForList(plan)}
        </MenuItem>,
      )
    }

    // Keep the currently selected plan visible even if it's no longer available.
    if (currentPaymentPlanId && !seenIds.has(currentPaymentPlanId)) {
      const fallback = plans.find((p) => (p.id as string) === currentPaymentPlanId)
      if (fallback) {
        items.push(
          <MenuItem key={fallback.id} value={fallback.id} disabled>
            {formatPaymentPlanForList(fallback)}
          </MenuItem>,
        )
      }
    }

    return items
  }, [t, plans, availablePaymentPlans, currentPaymentPlanId])

  // Promo plans force the term to the plan's loanTerm.
  const selectedPlan = useMemo(() => plans.find((p) => p.id === currentPaymentPlanId), [plans, currentPaymentPlanId])
  const onPromoPlan = isPromoPlan(currentPaymentPlanId)
  const { field: termField } = useController({
    control,
    name: 'term' as FieldPathByValue<T, number>,
  })
  useEffect(() => {
    if (!onPromoPlan || !selectedPlan) return
    if (termField.value !== selectedPlan.loanTerm) {
      termField.onChange(selectedPlan.loanTerm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPromoPlan, selectedPlan?.loanTerm])

  return (
    <Grid size={cellSize}>
      <ControlledSelect
        name={paymentPlanIdName}
        control={control}
        label={label ?? t('worksheetCommon.paymentPlan')}
        options={options}
        disableEmptyOption
        fullWidth
      />
    </Grid>
  )
}

type TermConstraint = FieldValues & {
  term: number | null
  paymentPlanId: string
  paymentFrequency: EPaymentFrequency
  activationDate: string
  firstPaymentDate: DateOnly | null
  amountRequested: number
  includeProtection: boolean
}

const TERM_PREREQUISITES = [
  { name: 'paymentPlanId', labelKey: 'paymentPlan' },
  { name: 'paymentFrequency' },
  { name: 'activationDate' },
  { name: 'firstPaymentDate' },
  { name: 'amountRequested' },
] as const satisfies readonly FieldPrerequisiteSpec<TermConstraint>[]

const TERM_WATCH = ['includeProtection', 'term'] as const

interface TermProps<T extends TermConstraint> {
  control: Control<T>
  label?: string
  disabled?: boolean
  // Extra upper cap from the consumer, composed with Base's caps via Math.min.
  maxTerm?: number
}

const Term = <T extends TermConstraint>({ control, label, disabled, maxTerm }: TermProps<T>) => {
  const { t } = useTranslation()
  const fieldLabel = label ?? t('worksheetCommon.term')
  const termName = 'term' as FieldPathByValue<T, number>

  return (
    <FieldReadyGate
      control={control}
      requiredFields={TERM_PREREQUISITES}
      watch={TERM_WATCH}
      selfName="term"
      shell={
        <ControlledSelect
          name={termName}
          control={control}
          label={fieldLabel}
          options={[]}
          parseValue={(v) => (v !== null && v !== '' ? Number(v) : null)}
          disabled
          disableEmptyOption
          fullWidth
        />
      }
    >
      {(values) => (
        <TermActive
          control={control}
          fieldLabel={fieldLabel}
          disabled={!!disabled}
          maxTerm={maxTerm}
          paymentPlanId={values.paymentPlanId}
          paymentFrequency={values.paymentFrequency}
          activationDate={values.activationDate}
          firstPaymentDate={values.firstPaymentDate!}
          amountRequested={values.amountRequested}
          includeProtection={values.includeProtection}
          currentTerm={values.term}
        />
      )}
    </FieldReadyGate>
  )
}

interface TermActiveProps<T extends TermConstraint> {
  control: Control<T>
  fieldLabel: string
  disabled: boolean
  maxTerm?: number
  paymentPlanId: string
  paymentFrequency: EPaymentFrequency
  activationDate: string
  firstPaymentDate: DateOnly
  amountRequested: number
  includeProtection: boolean
  currentTerm: number | null
}

// Memoized: FieldReadyGateActive's useWatch produces fresh values arrays on
// submit-related broadcasts; memo turns those into a shallow bailout.
const TermActiveImpl = <T extends TermConstraint>({
  control,
  fieldLabel,
  disabled,
  maxTerm,
  paymentPlanId,
  paymentFrequency,
  activationDate,
  firstPaymentDate,
  amountRequested,
  includeProtection,
  currentTerm,
}: TermActiveProps<T>) => {
  const { t } = useTranslation()
  const {
    supportedTerms,
    minLoanTerm,
    maxLoanTerm,
    largeLoanAmount,
    longTerm,
    creditApplicationId,
    financingProgramId,
    interestRate,
    hasCoApplicant,
    stateIso,
    maxPmtAmount: rawMaxPmtAmount,
    merchantId,
    maxTermDuration: decisionMaxTerm,
    useAgentLeeway,
    validateMaxPayment,
  } = useBaseWorksheetFormConfig()

  // useAgentLeeway raises the effective payment ceiling by a per-plan margin.
  const maxPmtAmount = useMemo(
    () => (useAgentLeeway ? getMaxPmtAmountWithAgentLeeway(paymentPlanId, rawMaxPmtAmount) : rawMaxPmtAmount),
    [useAgentLeeway, paymentPlanId, rawMaxPmtAmount],
  )

  const allowedTerms = useMemo(() => {
    const programMin = minLoanTerm > 0 ? minLoanTerm : 0
    const caps: number[] = []
    if (maxLoanTerm > 0) caps.push(maxLoanTerm)
    if (decisionMaxTerm) caps.push(decisionMaxTerm)
    if (maxTerm && maxTerm > 0) caps.push(maxTerm)
    if (largeLoanAmount > 0 && longTerm > 0) {
      caps.push((amountRequested ?? 0) >= largeLoanAmount ? longTerm : SHORT_TERM_CAP)
    }
    return supportedTerms.filter((term) => {
      const value = Number(term)
      if (value < programMin) return false
      return caps.every((cap) => value <= cap)
    })
  }, [supportedTerms, minLoanTerm, maxLoanTerm, largeLoanAmount, longTerm, decisionMaxTerm, maxTerm, amountRequested])

  // Promo plans force the term to plan.loanTerm (sync lives in PaymentPlan).
  const isLockedByPromoPlan = isPromoPlan(paymentPlanId)

  const validTermsArgs = useMemo(() => {
    if (!validateMaxPayment) return null
    if (!paymentPlanId || !paymentFrequency || !activationDate || !firstPaymentDate || !amountRequested) {
      return null
    }
    return {
      creditApplicationId,
      financingProgramId,
      interestRate,
      hasCoApplicant,
      stateIso,
      maxPmtAmount,
      merchantId,
      paymentFrequency,
      paymentPlanId,
      deliveryOn: new Date(activationDate),
      firstPaymentOn: firstPaymentDate.toDate(),
      amountRequested,
      includeInsurance: includeProtection ?? false,
      possibleTerms: allowedTerms,
    }
  }, [
    validateMaxPayment,
    creditApplicationId,
    financingProgramId,
    interestRate,
    hasCoApplicant,
    stateIso,
    maxPmtAmount,
    merchantId,
    paymentFrequency,
    paymentPlanId,
    activationDate,
    firstPaymentDate,
    amountRequested,
    includeProtection,
    allowedTerms,
  ])

  const [validTerms, invalidTerms, isLoadingTerms] = useValidTerms(validTermsArgs)
  useReportFieldLoading('term', isLoadingTerms)

  // Apply validation only once the server returns a definitive result.
  // Loading and feature-flag-off paths leave options enabled.
  const applyValidation = !isLoadingTerms && (validTerms.size > 0 || invalidTerms.size > 0)

  const options = useMemo(
    () =>
      allowedTerms.map((term) => {
        const isInvalid = applyValidation && !validTerms.has(term)
        const reasonKey = isInvalid ? invalidTerms.get(term) : undefined
        const tooltipText = translateInvalidTermReason(t as TranslateFn, reasonKey)
        return (
          <MenuItem key={term} value={term} disabled={isInvalid}>
            {tooltipText ? (
              <Tooltip title={tooltipText} placement="right">
                <span>{term}</span>
              </Tooltip>
            ) : (
              term
            )}
          </MenuItem>
        )
      }),
    [allowedTerms, applyValidation, validTerms, invalidTerms, t],
  )

  const currentReason = applyValidation && currentTerm ? invalidTerms.get(currentTerm) : undefined
  const noValidTermsRemain = applyValidation && validTerms.size === 0 && invalidTerms.size > 0
  const paymentError = useMemo<WorksheetError | null>(() => {
    if (currentReason === 'pmtTooHigh' && currentTerm) {
      return {
        code: 'pmtTooHigh',
        messageKey: 'worksheetCommon.errors.pmtTooHigh',
        messageArgs: { term: currentTerm, maxPayment: maxPmtAmount },
      }
    }
    if (noValidTermsRemain) {
      return {
        code: 'noValidTerm',
        messageKey: 'worksheetCommon.errors.noValidTerm',
        messageArgs: { maxPayment: maxPmtAmount },
      }
    }
    return null
  }, [currentReason, currentTerm, noValidTermsRemain, maxPmtAmount])
  useReportError('term:paymentExceeded', paymentError)

  return (
    <ControlledSelect
      name={'term' as FieldPathByValue<T, number>}
      control={control}
      label={fieldLabel}
      options={options}
      parseValue={(v) => (v !== null && v !== '' ? Number(v) : null)}
      disabled={disabled || isLockedByPromoPlan}
      disableEmptyOption
      fullWidth
    />
  )
}

const TermActive = memo(TermActiveImpl) as typeof TermActiveImpl

interface PaymentFrequencyProps<T extends FieldValues & { paymentFrequency: EPaymentFrequency }> {
  control: Control<T>
  locked?: boolean
  label?: string
}

const PaymentFrequency = <T extends FieldValues & { paymentFrequency: EPaymentFrequency }>({
  control,
  locked,
  label,
}: PaymentFrequencyProps<T>) => {
  const { t } = useTranslation()
  const { supportedPaymentFrequencies, isSinglePayment } = useBaseWorksheetFormConfig()
  const frequencies = useMemo(
    () => (isSinglePayment ? [EPaymentFrequency.Single] : supportedPaymentFrequencies),
    [isSinglePayment, supportedPaymentFrequencies],
  )
  const options = useTranslatedOptionEnum('EPaymentFrequency', frequencies, 'enum')
  const isLocked = locked ?? frequencies.length <= 1

  const fieldName = 'paymentFrequency' as FieldPathByValue<T, EPaymentFrequency>
  const { field } = useController({ control, name: fieldName })

  useEffect(() => {
    if (isSinglePayment && field.value !== EPaymentFrequency.Single) {
      field.onChange(EPaymentFrequency.Single)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSinglePayment])

  return (
    <Grid size={cellSize}>
      <ControlledSelect
        name={fieldName}
        control={control}
        label={label ?? t('worksheetCommon.paymentFrequency')}
        options={options}
        disabled={isLocked}
        disableEmptyOption
        fullWidth
      />
    </Grid>
  )
}

interface ActivationDateProps<T extends FieldValues & { activationDate: string }> {
  control: Control<T>
  maxDate?: DateOnly
  label?: string
}

const ActivationDate = <T extends FieldValues & { activationDate: string }>({
  control,
  maxDate,
  label,
}: ActivationDateProps<T>) => {
  const { t } = useTranslation()
  const { editLocked } = useBaseWorksheetFormConfig()
  return (
    <Grid size={cellSize}>
      <ControlledStringDateOnlyPicker
        name={'activationDate' as FieldPathByValue<T, string>}
        control={control}
        label={label ?? t('worksheetCommon.activationDate')}
        disablePast
        maxDate={maxDate}
        disabled={!!editLocked}
        fullWidth
      />
    </Grid>
  )
}

type FirstPaymentDateConstraint = FieldValues & {
  firstPaymentDate: DateOnly | null
  activationDate: string
  paymentFrequency: EPaymentFrequency
  // Optional so worksheets without the toggle still satisfy the type.
  isHighEstimate?: boolean
}

const FIRST_PAYMENT_DATE_PREREQUISITES = [
  { name: 'activationDate' },
  { name: 'paymentFrequency' },
] as const satisfies readonly FieldPrerequisiteSpec<FirstPaymentDateConstraint>[]

const FIRST_PAYMENT_DATE_WATCH = ['isHighEstimate'] as const

interface FirstPaymentDateProps<T extends FirstPaymentDateConstraint> {
  control: Control<T>
  label?: string
}

const FirstPaymentDate = <T extends FirstPaymentDateConstraint>({ control, label }: FirstPaymentDateProps<T>) => {
  const { t } = useTranslation()
  const fieldName = 'firstPaymentDate' as FieldPathByValue<T, DateOnly | null>
  const fieldLabel = label ?? t('worksheetCommon.firstPaymentDate')

  return (
    <FieldReadyGate
      control={control}
      requiredFields={FIRST_PAYMENT_DATE_PREREQUISITES}
      watch={FIRST_PAYMENT_DATE_WATCH}
      selfName="firstPaymentDate"
      shell={
        <ControlledSelect
          name={fieldName}
          control={control}
          label={fieldLabel}
          options={[]}
          parseValue={(v) => (v ? new DateOnly(v) : null)}
          disabled
          disableEmptyOption
          fullWidth
        />
      }
    >
      {(values) => (
        <FirstPaymentDateActive
          control={control}
          fieldName={fieldName}
          fieldLabel={fieldLabel}
          activationDate={values.activationDate}
          paymentFrequency={values.paymentFrequency}
          isHighEstimate={values.isHighEstimate}
        />
      )}
    </FieldReadyGate>
  )
}

interface FirstPaymentDateActiveProps<T extends FirstPaymentDateConstraint> {
  control: Control<T>
  fieldName: FieldPathByValue<T, DateOnly | null>
  fieldLabel: string
  activationDate: string
  paymentFrequency: EPaymentFrequency
  isHighEstimate: boolean | undefined
}

const FirstPaymentDateActiveImpl = <T extends FirstPaymentDateConstraint>({
  control,
  fieldName,
  fieldLabel,
  activationDate,
  paymentFrequency,
  isHighEstimate,
}: FirstPaymentDateActiveProps<T>) => {
  const { financingProgramId, creditApplicationId } = useBaseWorksheetFormConfig()

  // Pass null when prereqs aren't filled so the hook disables itself.
  const optionsDto = useMemo(() => {
    if (!activationDate || !paymentFrequency) return null
    return {
      deliveryOn: activationDate,
      systemOriginId: EOriginSystemId.Backoffice,
      isHighEstimate: isHighEstimate ?? false,
      paymentFrequency,
    }
  }, [activationDate, paymentFrequency, isHighEstimate])

  const [apiOptions, isFetching] = useGetFirstPaymentDateOptions(financingProgramId, creditApplicationId, optionsDto)
  useReportFieldLoading('firstPaymentDate', isFetching)

  const dateOptions = useMemo(() => apiOptions.map((d) => new DateOnly(d)), [apiOptions])
  const allowedSet = useMemo(() => new Set(dateOptions.map((d) => d.toString())), [dateOptions])

  const { field } = useController({ control, name: fieldName })

  // Auto-correct stale or null values not in the current API list.
  useEffect(() => {
    if (dateOptions.length === 0) return
    const current = field.value as DateOnly | null | undefined
    const currentStr = current?.toString()
    const isInList = !!currentStr && allowedSet.has(currentStr)
    if (!isInList) field.onChange(dateOptions[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions])

  const selectOptions = useMemo(
    () =>
      dateOptions.map((d) => (
        <MenuItem key={d.toString()} value={d.toString()}>
          {formatDate(d)}
        </MenuItem>
      )),
    [dateOptions],
  )

  const useDatePicker = dateOptions.length > FIRST_PAYMENT_DATE_PICKER_THRESHOLD

  return useDatePicker ? (
    <ControlledDateOnlyPicker
      name={fieldName}
      control={control}
      label={fieldLabel}
      minDate={dateOptions[0]}
      maxDate={dateOptions[dateOptions.length - 1]}
      shouldDisableDate={(d: Date) => !allowedSet.has(new DateOnly(d).toString())}
      fullWidth
    />
  ) : (
    <ControlledSelect
      name={fieldName}
      control={control}
      label={fieldLabel}
      options={selectOptions}
      parseValue={(v) => (v ? new DateOnly(v) : null)}
      disableEmptyOption
      fullWidth
    />
  )
}

const FirstPaymentDateActive = memo(FirstPaymentDateActiveImpl) as typeof FirstPaymentDateActiveImpl

interface IsHighEstimateProps<T extends FieldValues & { isHighEstimate: boolean }> {
  control: Control<T>
  label?: string
}

const IsHighEstimate = <T extends FieldValues & { isHighEstimate: boolean }>({
  control,
  label,
}: IsHighEstimateProps<T>) => {
  const { t } = useTranslation()
  const { canAcceptHighEstimate } = useBaseWorksheetFormConfig()
  const featureFlags = useFeatureFlags()

  const fieldName = 'isHighEstimate' as FieldPathByValue<T, boolean>
  const { field } = useController({ control, name: fieldName })
  const paymentPlanId = useWatch({ control, name: 'paymentPlanId' as FieldPath<T> }) as string | undefined
  const onPromoPlan = isPromoPlan(paymentPlanId)

  // useHighEstimateOnBuyDown OFF + promo plan → force isHighEstimate false.
  useEffect(() => {
    if (!featureFlags.useHighEstimateOnBuyDown && onPromoPlan && field.value) {
      field.onChange(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.useHighEstimateOnBuyDown, onPromoPlan])

  if (canAcceptHighEstimate !== true) return null

  return (
    <Grid size={cellSize}>
      <ControlledCheckbox
        name={fieldName}
        control={control}
        label={label ?? t('worksheetCommon.checkIsHighEstimate')}
      />
    </Grid>
  )
}

interface IncludeProtectionProps<T extends FieldValues & { includeProtection: boolean }> {
  control: Control<T>
  label?: string
}

const IncludeProtection = <T extends FieldValues & { includeProtection: boolean }>({
  control,
  label,
}: IncludeProtectionProps<T>) => {
  const { t } = useTranslation()
  const { supportsInsurance } = useBaseWorksheetFormConfig()

  if (!supportsInsurance) return null

  return (
    <Grid size={cellSize}>
      <ControlledCheckbox
        name={'includeProtection' as FieldPathByValue<T, boolean>}
        control={control}
        label={label ?? t('worksheetCommon.protectionPlan')}
      />
    </Grid>
  )
}

BaseWorksheetForm.AmountRequested = memo(AmountRequested) as typeof AmountRequested
BaseWorksheetForm.PaymentPlan = memo(PaymentPlan) as typeof PaymentPlan
BaseWorksheetForm.Term = memo(Term) as typeof Term
BaseWorksheetForm.PaymentFrequency = memo(PaymentFrequency) as typeof PaymentFrequency
BaseWorksheetForm.ActivationDate = memo(ActivationDate) as typeof ActivationDate
BaseWorksheetForm.FirstPaymentDate = memo(FirstPaymentDate) as typeof FirstPaymentDate
BaseWorksheetForm.IncludeProtection = memo(IncludeProtection) as typeof IncludeProtection
BaseWorksheetForm.IsHighEstimate = memo(IsHighEstimate) as typeof IsHighEstimate

export default BaseWorksheetForm
